import React, { useEffect, useState } from 'react';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Box, { BoxProps } from '@mui/material/Box';
import { Control, Controller, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import { Lead, LeadScreenStore } from 'src/stores/LeadStore';
import { client } from 'src/utils/axios-client';
import { optionsEndpoints } from 'src/configs/endpoints';
import { SelectOption } from 'src/types/common';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { IconButton, Tooltip } from '@mui/material';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import SectionAppBar from '../../../v2/components/Section/SectionAppBar';
import CloseButton from '../../../v2/components/Button/CloseButton';
import StandardButton from '../../../v2/components/Button/StandardButton';
import LoadingSpinner from '../../../components/LoadingSpinner';
import AccountSelect from '../../../v2/features/Accounts/AccountSelect/AccountSelect';
import { useGetInfiniteLeads } from '../../../hooks/lead-workview-hooks';


type LeadFormProps = {
    onSubmit: any;
    control: Control<Lead, any>;
    errors: FieldErrors<Lead>;
    handleClose: () => void;
    lead: Lead;
};
const industryDefault = {
    industry: {
        id: '00000000-0000-0000-0000-000000000000',
        name: 'None',
    },
};

const LeadForm = ({ onSubmit, control, errors, handleClose, lead }: LeadFormProps) => {
    const [industries, setIndustries] = useState([] as SelectOption[]);
    const [countries, setCountries] = useState([] as SelectOption[]);
    const [levels, setLevels] = useState([] as SelectOption[]);
    const [functions, setFunctions] = useState([] as SelectOption[]);
    const [sources, setsources] = useState([] as SelectOption[]);
    const [accounts, setAccounts] = useState([] as SelectOption[]);
    const [stages, setStages] = useState([] as SelectOption[]);
    const [sprints, setSprints] = useState([] as SelectOption[]);
    const [regions, setRegions] = useState([] as SelectOption[]);
    const [owners, setOwners] = useState([] as SelectOption[]);
    const [dncMobile, setDncMobile] = useState<boolean>(lead.dncMobile);
    const [pageSize, setPageSize] = useState(50);

    useEffect(() => {
        const load = async () => {
            const response = await client.get(optionsEndpoints.industryEndpoint);
            setIndustries(response.data);

            const response2 = await client.get(optionsEndpoints.countryEndpoint);
            setCountries(response2.data);

            const response3 = await client.get(optionsEndpoints.leadLevelEndpoint);
            setLevels(response3.data);

            const response4 = await client.get(optionsEndpoints.leadFunctionEndpoint);
            setFunctions(response4.data);

            const response5 = await client.get(optionsEndpoints.leadSourceEndpoint);
            setsources(response5.data);

            const response6 = await client.get(optionsEndpoints.accountEndpoint);
            setAccounts(response6.data);

            const response7 = await client.get(optionsEndpoints.leadStageEndpoint);
            setStages(response7.data);

            const response9 = await client.get(optionsEndpoints.regionEndpoint);
            setRegions(response9.data);

            const response10 = await client.get(optionsEndpoints.ownerEndpoint);
            setOwners(response10.data);

            const response8 = await client.get(
                '/api/options/{customerId}/sprint'.replace('{customerId}', lead.customer.id),
            );
            setSprints(response8.data);
        };

        load();
    }, [lead]);

    const handleDncMobileChange = (event: { target: { checked: boolean | ((prevState: boolean) => boolean) } }) => {
        setDncMobile(event.target.checked);
    };

    const handleAddAccount = (newAccountName: string, accountId: string) => {
        const newAccount: SelectOption = { value: accountId, name: newAccountName };
        setAccounts([...accounts, newAccount]); // Add new account to the list
    };

    if (industries.length === 0 || countries.length === 0 || levels.length === 0) return <LoadingSpinner />;

    return (
        <>
            <form noValidate autoComplete="off" onSubmit={onSubmit}>
                <SectionAppBar
                    title={'Edit Lead'}
                    buttons={
                        <>
                            <StandardButton type="submit" variant="contained" color="info" icon={<SaveTwoToneIcon />}>
                                Save
                            </StandardButton>

                            <CloseButton onClick={handleClose}>Cancel</CloseButton>
                        </>
                    }
                    subTitle=""
                />
                <PerfectScrollbar options={{ wheelPropagation: false }}>
                    <Box sx={{ p: 5 }}>
                        <Grid container spacing={2} justifyContent={'center'}>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <AccountSelect
                                    accounts={accounts}
                                    control={control}
                                    errors={errors}
                                    onAddAccount={handleAddAccount}
                                />
                                {/*Account*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }} disabled>
                                    <InputLabel id="plan-select">Stage</InputLabel>
                                    <Controller
                                        name="leadStage.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Stage"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{ placeholder: 'Select Stage', 'data-testid': 'leadStage' }}
                                            >
                                                {stages.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.leadStage && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Stage*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }} disabled>
                                    <Controller
                                        name="sprint.name"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <TextField value={value} label="Sprint" disabled size="small" />
                                            //<Select
                                            //    fullWidth
                                            //    size='small'
                                            //    value={value}
                                            //    defaultValue={value}
                                            //    label='Select Sprint'
                                            //    onBlur={onBlur}
                                            //    onChange={onChange}
                                            //    inputProps={{ placeholder: 'Select Sprint', 'data-testid': 'sprint' }}
                                            //>
                                            //    <MenuItem value="3616275b-74ee-4fa7-ba75-ad68e5bca4cd">
                                            //        <em>None</em>
                                            //    </MenuItem>
                                            //    {sprints.map(option => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
                                            //</Select>
                                        )}
                                    />
                                    {errors.sprint && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Sprint*/}
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                name="firstName"
                                                size="small"
                                                value={value}
                                                label="First Name"
                                                onChange={onChange}
                                                placeholder="First Name"
                                                inputProps={{ 'data-testid': 'first-name' }}
                                                error={Boolean(errors.firstName)}
                                            />
                                        )}
                                    />
                                    {errors.firstName && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.firstName.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*First Name*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Last Name"
                                                onChange={onChange}
                                                placeholder="Last Name"
                                                inputProps={{ 'data-testid': 'last-name' }}
                                                error={Boolean(errors.firstName)}
                                            />
                                        )}
                                    />
                                    {errors.lastName && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.lastName.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Last Name*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="salutation"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Salutation"
                                                onChange={onChange}
                                                placeholder="Salutation"
                                                inputProps={{ 'data-testid': 'salutation' }}
                                                error={Boolean(errors.salutation)}
                                            />
                                        )}
                                    />
                                    {errors.salutation && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.salutation.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Salutation*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="jobTitle"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Job Title"
                                                onChange={onChange}
                                                placeholder="Job Title"
                                                inputProps={{ 'data-testid': 'job-title' }}
                                                error={Boolean(errors.firstName)}
                                            />
                                        )}
                                    />
                                    {errors.jobTitle && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.jobTitle.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Job Title*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="emailAddress"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Email Address"
                                                onChange={onChange}
                                                placeholder="Email Address"
                                                error={Boolean(errors.firstName)}
                                                inputProps={{ 'data-testid': 'email-address' }}
                                            />
                                        )}
                                    />
                                    {errors.emailAddress && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.emailAddress.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Email Address*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="company"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Company"
                                                onChange={onChange}
                                                placeholder="Job Title"
                                                inputProps={{ 'data-testid': 'company' }}
                                                error={Boolean(errors.company)}
                                            />
                                        )}
                                    />
                                    {errors.company && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.company.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Company*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="addressStreet"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Street Address"
                                                onChange={onChange}
                                                placeholder="Street Address"
                                                inputProps={{ 'data-testid': 'addressStreet' }}
                                                error={Boolean(errors.addressStreet)}
                                            />
                                        )}
                                    />
                                    {errors.addressStreet && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.addressStreet.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address Street*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="addressCity"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="City"
                                                onChange={onChange}
                                                placeholder="City"
                                                inputProps={{ 'data-testid': 'addressCity' }}
                                                error={Boolean(errors.addressCity)}
                                            />
                                        )}
                                    />
                                    {errors.addressCity && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.addressCity.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address City*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="addressProvince"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Province"
                                                onChange={onChange}
                                                placeholder="Province"
                                                inputProps={{ 'data-testid': 'addressProvince' }}
                                                error={Boolean(errors.addressProvince)}
                                            />
                                        )}
                                    />
                                    {errors.addressProvince && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.addressProvince.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address Province*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="addressPostalCode"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Postal Code"
                                                onChange={onChange}
                                                placeholder="Postal Code"
                                                inputProps={{ 'data-testid': 'addressPostalCode' }}
                                                error={Boolean(errors.addressPostalCode)}
                                            />
                                        )}
                                    />
                                    {errors.addressPostalCode && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.addressPostalCode.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address Postal Code*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Country</InputLabel>
                                    <Controller
                                        name="country.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Country"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{ placeholder: 'Select Country', 'data-testid': 'country' }}
                                            >
                                                {countries.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.country && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address Country*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Region</InputLabel>
                                    <Controller
                                        name="addressRegion.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Region"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{
                                                    placeholder: 'Select Region',
                                                    'data-testid': 'addressRegion',
                                                }}
                                                error={Boolean(errors.addressRegion)}
                                            >
                                                {regions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.addressRegion && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Address Region*/}
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="directPhone"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Direct Phone"
                                                onChange={onChange}
                                                placeholder="Direct Phone"
                                                inputProps={{ 'data-testid': 'directPhone' }}
                                                error={Boolean(errors.directPhone)}
                                            />
                                        )}
                                    />
                                    {errors.directPhone && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.directPhone.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Direct*/}
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth>
                                    <Controller
                                        name="dncPhone"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <FormControlLabel
                                                control={<Switch checked={value} onChange={onChange} />}
                                                label="DNC"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="mobilePhone"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Mobile Phone"
                                                onChange={onChange}
                                                placeholder="Mobile Phone"
                                                inputProps={{ 'data-testid': 'mobilePhone' }}
                                                error={Boolean(errors.mobilePhone)}
                                            />
                                        )}
                                    />
                                    {errors.mobilePhone && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.mobilePhone.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Mobile*/}
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth>
                                    <Controller
                                        name="dncMobile"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <FormControlLabel
                                                control={<Switch checked={value} onChange={onChange} />}
                                                label="DNC"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="switchboardPhone"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Switchboard"
                                                onChange={onChange}
                                                placeholder="Switchboard"
                                                inputProps={{ 'data-testid': 'switchboardPhone' }}
                                                error={Boolean(errors.switchboardPhone)}
                                            />
                                        )}
                                    />
                                    {errors.switchboardPhone && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.switchboardPhone.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Switchboard*/}
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth>
                                    <Controller
                                        name="dncSwitchboard"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <FormControlLabel
                                                control={<Switch checked={value} onChange={onChange} />}
                                                label="DNC"
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                            <Grid
                                item
                                xs={2}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                            <Grid
                                item
                                xs={8}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="website"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Website"
                                                onChange={onChange}
                                                placeholder="Website"
                                                inputProps={{ 'data-testid': 'website' }}
                                                error={Boolean(errors.website)}
                                            />
                                        )}
                                    />
                                    {errors.website && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.website.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {/*Website*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                            <Grid
                                item
                                xs={8}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="linkedIn"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="LinkedIn"
                                                onChange={onChange}
                                                placeholder="LinkedIn"
                                                inputProps={{ 'data-testid': 'linkedIn' }}
                                                error={Boolean(errors.linkedIn)}
                                            />
                                        )}
                                    />
                                    {errors.linkedIn && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.linkedIn.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {/*LinkedIn*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                            <Grid
                                item
                                xs={8}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <Controller
                                        name="switchboardEmail"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange } }) => (
                                            <TextField
                                                value={value}
                                                size="small"
                                                label="Switchboard Email"
                                                onChange={onChange}
                                                placeholder="Switchboard Email"
                                                inputProps={{ 'data-testid': 'switchboardEmail' }}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <Divider />
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Industry</InputLabel>
                                    <Controller
                                        name="industry.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Industry"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{ placeholder: 'None', 'data-testid': 'None' }}
                                            >
                                                {industries.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.industry && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Industry*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Level</InputLabel>
                                    <Controller
                                        name="leadLevel.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Level"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{ placeholder: 'Select Level', 'data-testid': 'leadLevel' }}
                                                error={Boolean(errors.leadLevel)}
                                            >
                                                {levels.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.leadLevel && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Level*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Function</InputLabel>
                                    <Controller
                                        name="leadFunction.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Function"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{
                                                    placeholder: 'Select Function',
                                                    'data-testid': 'leadFunction',
                                                }}
                                                error={Boolean(errors.leadFunction)}
                                            >
                                                {functions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.leadFunction && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Function*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 1 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Owner</InputLabel>
                                    <Controller
                                        name="owner.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select owner"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{ placeholder: 'Select Owner', 'data-testid': 'owner' }}
                                                error={Boolean(errors.owner)}
                                            >
                                                <MenuItem value="3616275b-74ee-4fa7-ba75-ad68e5bca4cd">
                                                    <em>None</em>
                                                </MenuItem>
                                                {owners.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.owner && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Owner*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            >
                                <FormControl fullWidth sx={{ mb: 1 }}>
                                    <InputLabel id="plan-select">Source</InputLabel>
                                    <Controller
                                        name="leadSource.id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field: { value, onChange, onBlur } }) => (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={value}
                                                label="Select Source"
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                inputProps={{
                                                    placeholder: 'Select Source',
                                                    'data-testid': 'leadSource',
                                                }}
                                                error={Boolean(errors.leadSource)}
                                            >
                                                {sources.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.leadSource && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            Please select a value!
                                        </FormHelperText>
                                    )}
                                </FormControl>{' '}
                                {/*Source*/}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                sx={{ pt: 4, display: 'flex', alignItems: 'left', flexDirection: 'column', mb: 2 }}
                            ></Grid>
                        </Grid>
                    </Box>
                </PerfectScrollbar>
            </form>
        </>
    );
};

export default LeadForm;
